import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CheckCircle, ChevronDown, ChevronRight, Download, Mic, Search } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

export const formatPhone = (p) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p || '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (p || '-');
};

const formatDuration = (s) => {
  if (!s && s !== 0) return '-';
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const STATUS = {
  saved: ['Saved', 'bg-green-50 text-green-700'],
  recording: ['Recording', 'bg-blue-50 text-blue-700'],
  ready: ['Saving…', 'bg-blue-50 text-blue-700'],
  failed: ['Could not save', 'bg-red-50 text-red-700'],
  discarded: ['Not kept (card entry)', 'bg-gray-100 text-gray-600'],
  not_recorded: ['Not recorded', 'bg-gray-100 text-gray-600'],
};

/** Audio is behind the admin login, so it's fetched with the token and played from a blob. */
function AudioPlayer({ url }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let alive = true;
    setSrc(null);
    setError(false);
    axios.get(url, { responseType: 'blob' })
      .then((r) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (error) return <p className="text-xs text-red-500">Audio not available</p>;
  if (!src) return <p className="text-xs text-ink/40">Loading audio…</p>;
  return <audio controls preload="metadata" src={src} className="w-full max-w-xl h-10" />;
}

function Clip({ clip, onSaved }) {
  const [text, setText] = useState(clip.corrected ?? '');
  const [play, setPlay] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/admin/voice-clips/${clip.id}`, patch);
      onSaved(r.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  let heard = clip.heard || '';
  try {
    const parsed = JSON.parse(heard);
    if (parsed && typeof parsed === 'object') heard = parsed.transcript || heard;
  } catch { /* plain text */ }

  return (
    <div className="border border-border rounded-lg p-3 bg-paper/40">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-medium">
          {clip.label || 'Spoken answer'}
          <span className="ml-2 text-xs text-ink/50">{clip.kind === 'voice_fill' ? 'all details' : 'one field'} · {formatDuration(clip.duration)}</span>
        </div>
        {clip.reviewed && <span className="text-xs text-green-600 inline-flex items-center"><CheckCircle className="h-3 w-3 mr-1" />Reviewed</span>}
      </div>
      <div className="mt-2">
        {play
          ? <AudioPlayer url={`${API_URL}/admin/voice-clips/${clip.id}/audio`} />
          : <Button size="sm" variant="outline" onClick={() => setPlay(true)}><Mic className="h-4 w-4 mr-1" />Play</Button>}
      </div>
      <p className="mt-2 text-sm"><span className="text-ink/50">Heard: </span><span dir="auto">{heard || '(nothing)'}</span></p>
      <div className="mt-2 flex gap-2 items-start flex-wrap">
        <Input
          dir="auto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What was really said (leave empty if it was heard right)"
          className="flex-1 min-w-[220px]"
        />
        <Button size="sm" disabled={saving} onClick={() => save({ corrected: text.trim() || null, reviewed: true })}>
          {text.trim() ? 'Save correction' : 'Heard right'}
        </Button>
      </div>
    </div>
  );
}

function CallDetail({ callSid, onChanged }) {
  const [call, setCall] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/admin/calls/${callSid}`)
      .then((r) => { setCall(r.data); setNotes(r.data.notes || ''); })
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load call')));
  }, [callSid]);

  const update = async (patch) => {
    try {
      const r = await axios.put(`${API_URL}/admin/calls/${callSid}`, patch);
      setCall(r.data);
      onChanged(r.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save'));
    }
  };

  if (!call) return <div className="p-4 text-sm text-ink/50">Loading…</div>;
  return (
    <div className="p-4 space-y-4 bg-white">
      {call.status === 'saved'
        ? <AudioPlayer url={`${API_URL}/admin/calls/${callSid}/audio`} />
        : <p className="text-sm text-ink/50">No recording for this call{call.status === 'recording' ? ' yet' : ''}.</p>}
      {call.channels === 2 && <p className="text-xs text-ink/40">Two channels: the caller on one side, the phone system on the other.</p>}
      <div className="flex gap-2 items-start flex-wrap">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" className="flex-1 min-w-[260px]" rows={2} />
        <div className="flex flex-col gap-2">
          <Button size="sm" variant="outline" onClick={() => update({ notes })}>Save notes</Button>
          <Button size="sm" onClick={() => update({ reviewed: !call.reviewed })}>
            {call.reviewed ? 'Mark not reviewed' : 'Mark reviewed'}
          </Button>
        </div>
      </div>
      {call.clips?.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-ink/70">Spoken answers ({call.clips.length})</h3>
          <p className="text-xs text-ink/50">Correct what the system heard; corrected clips become training data.</p>
          {call.clips.map((clip) => (
            <Clip key={clip.id} clip={clip} onSaved={(c) => setCall((prev) => ({
              ...prev, clips: prev.clips.map((x) => (x.id === c.id ? c : x)),
            }))} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCalls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [filter, setFilter] = useState('all'); // all | todo | reviewed
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [openSid, setOpenSid] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const params = useCallback((before) => {
    const p = { limit: 50 };
    if (phone.trim()) p.phone = phone.trim();
    if (filter === 'todo') p.reviewed = false;
    if (filter === 'reviewed') p.reviewed = true;
    if (before) p.before = before;
    return p;
  }, [phone, filter]);

  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/calls`, { params: params(before) });
      setRecordingEnabled(r.data.recording_enabled);
      setCalls((prev) => (before ? [...prev, ...r.data.calls] : r.data.calls));
      setHasMore(r.data.calls.length === 50);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load calls'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRecording = async (enabled) => {
    try {
      const r = await axios.put(`${API_URL}/admin/call-recording`, { enabled });
      setRecordingEnabled(r.data.recording_enabled);
      toast.success(enabled ? 'New calls will be recorded' : 'Call recording is off');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to change setting'));
    }
  };

  const exportClips = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/voice-clips/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'voice-clips.jsonl';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Export failed'));
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold text-ink mb-2">Calls</h1>
              <p className="text-ink/60">Recordings of phone-line calls and the answers callers spoke, for review and training.</p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={recordingEnabled} onCheckedChange={toggleRecording} data-testid="call-recording-switch" />
                Record calls
              </label>
              <Button variant="outline" size="sm" onClick={exportClips}>
                <Download className="h-4 w-4 mr-1" />Training list
              </Button>
            </div>
          </div>

          <div className="flex gap-4 mb-6 items-center flex-wrap">
            <form className="relative max-w-xs flex-1" onSubmit={(e) => { e.preventDefault(); load(); }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Caller number" value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-10" />
            </form>
            <div className="flex gap-2">
              {[['all', 'All'], ['todo', 'To review'], ['reviewed', 'Reviewed']].map(([key, label]) => (
                <Button key={key} size="sm" variant={filter === key ? 'default' : 'outline'} onClick={() => setFilter(key)}>{label}</Button>
              ))}
            </div>
          </div>

          {loading && calls.length === 0 ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : calls.length === 0 ? (
            <div className="bg-white rounded-lg border border-border p-12 text-center text-ink/60">No calls yet</div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Date</TableHead>
                    <TableHead>Caller</TableHead>
                    <TableHead>Length</TableHead>
                    <TableHead>Recording</TableHead>
                    <TableHead>Spoken answers</TableHead>
                    <TableHead>Reviewed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calls.map((c) => {
                    const [label, color] = STATUS[c.status] || [c.status, 'bg-gray-100 text-gray-600'];
                    const open = openSid === c.call_sid;
                    return [
                      <TableRow key={c.call_sid} className="cursor-pointer" onClick={() => setOpenSid(open ? null : c.call_sid)}>
                        <TableCell>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap text-ink/70">{new Date(c.created_at).toLocaleString()}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatPhone(c.from_number)}</TableCell>
                        <TableCell>{formatDuration(c.duration)}</TableCell>
                        <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${color}`}>{label}</span></TableCell>
                        <TableCell>{c.clip_count || 0}</TableCell>
                        <TableCell>{c.reviewed ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span className="text-xs text-ink/30">-</span>}</TableCell>
                      </TableRow>,
                      open && (
                        <TableRow key={`${c.call_sid}-detail`}>
                          <TableCell colSpan={7} className="p-0">
                            <CallDetail callSid={c.call_sid} onChanged={(u) => setCalls((prev) => prev.map((x) => (x.call_sid === u.call_sid ? { ...x, ...u, clip_count: x.clip_count } : x)))} />
                          </TableCell>
                        </TableRow>
                      ),
                    ];
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {hasMore && (
            <div className="flex justify-center mt-4">
              <Button variant="outline" disabled={loading} onClick={() => load(calls[calls.length - 1]?.created_at)}>Load more</Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
